import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { fromEvent, Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { QPointIconModule } from '@qpoint/icons';
import { NgbTypeaheadModule } from '@ng-bootstrap/ng-bootstrap';
import { QPointLoadingSpinnerComponent } from '@qpoint/loading';

@Component({
    selector: 'qpoint-searchfield',
    templateUrl: './searchfield.component.html',
    styleUrls: ['./searchfield.component.scss'],
    imports: [
        CommonModule,
        FormsModule,
        QPointIconModule,
        NgbTypeaheadModule,
        QPointLoadingSpinnerComponent
    ]
})
export class QPointSearchfieldComponent implements AfterViewInit, OnInit {
  @Input() placeholder = '';
  @Input() searchTerm = '';
  @Input() searchActive: boolean;

  @Input() searchPattern: RegExp;
  @Input() searchPatternValidationMessage: string;

  @Input() suggestions: string[];

  @Input() autoSearchOnTextChange = false;
  @Output() open = new EventEmitter<boolean>();
  @Output() close = new EventEmitter<boolean>();
  @Output() searchTermModelChange = new EventEmitter();
  @Output() searchEventTriggered = new EventEmitter();
  @Output() doSearch = new EventEmitter<string>();
  @Output() searchPatternNotAllowed = new EventEmitter<string>();

  public isOpen = false;
  @ViewChild('searchInput')
  private searchInput: any;
  private searchSubject: Subject<string> = new Subject<string>();
  private searchInputCssTransitionEventSubscription: Subscription;

  constructor() {
  }

  ngOnInit(): void {
    if (this.searchTerm) {
      this.isOpen = true;
    }
  }

  public ngAfterViewInit(): void {
    this.searchSubject
      .pipe(
        debounceTime(200)
      )
      .subscribe(searchString => {
        if (!searchString) {
          this.doSearch.emit('');
          return;
        }
        searchString = searchString.trim();
        if (this.validateAllowedSearchPattern(searchString)) {
          this.doSearch.emit(searchString);
        }
      });
  }

  public onSearch() {
    if (!this.isOpen) {
      this.isOpen = !this.isOpen;
      this.open.emit(this.isOpen);
      this.searchInputCssTransitionEventSubscription = fromEvent(this.searchInput.nativeElement, 'transitionend')
        .subscribe(
          {
            next: () => {
              if (this.isOpen) {
                this.searchInput.nativeElement.focus();
                this.searchInputCssTransitionEventSubscription.unsubscribe();
              }
            }
            ,
            error: (error) => console.log(error)
          }
        );
    } else {
      this.searchEventTriggered.emit();
      this.searchSubject.next(this.searchTerm);
    }

  }

  public onClose() {
    if (this.isOpen) {
      this.isOpen = !this.isOpen;
      this.close.emit(this.isOpen);
    }
  }

  public onClear() {
    this.searchTerm = '';
    this.searchTermModelChange.emit(this.searchTerm);
    this.onSearch();
  }

  public onChange(newValue) {
    this.searchTerm = newValue;
    this.searchTermModelChange.emit(this.searchTerm);
    if (this.autoSearchOnTextChange) {
      this.searchSubject.next(this.searchTerm);
    }
  }

  public searchSuggestion = (text$: Observable<string>) => text$.pipe(
    debounceTime(200),
    distinctUntilChanged(),
    map(term => this.getSuggestionsOnTerm(term)));

  public getSuggestionsOnTerm(term: string) {
    return (term.length < 2 || !this.suggestions) ? []
      : this.suggestions.filter(v => v.toLowerCase().indexOf(term.toLowerCase()) > -1).slice(0, 10);
  }


  private validateAllowedSearchPattern(value) {
    const allowed = value === '' || !this.searchPattern || this.searchPattern.test(value);
    if (!allowed) {
      this.searchPatternNotAllowed
        .emit(this.searchPatternValidationMessage ? this.searchPatternValidationMessage : 'Search Pattern not allowed');
      //
      // this.notificationService
      //   .infoTranslated(this.searchPatternValidationMessage ? this.searchPatternValidationMessage : 'Search Pattern not allowed');
    }
    return allowed;
  }

}
